import React, { useState, useEffect, useCallback, useMemo } from "react";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import Sidebar from "../../../components/layout/Sidebar";
import Select from "react-select";
import { Row, Col, Form } from "react-bootstrap";
import axios from "axios";
import { BASE_URL } from "../../../env";
import { IoSearch } from "react-icons/io5";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TransferCroCenterWise = () => {
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [fromCroOptions, setFromCroOptions] = useState([]);
  const [toCroOptions, setToCroOptions] = useState([]);
  const [selectedFromCro, setSelectedFromCro] = useState(null);
  const [selectedToCro, setSelectedToCro] = useState(null);
  const [centers, setCenters] = useState([]);
  const [selectedCenters, setSelectedCenters] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const transformCroData = (data) =>
    data.map((item) => ({
      value: item.id,
      label: item.username,
      branchId: item.branchId,
    }));

  const getToCroData = async (branchId, croId) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/getCroData`, {
        params: { branchId, croId, transferType: "center" },
      });
      if (response.data.message) {
        setToCroOptions(transformCroData(response.data.message));
      } else {
        setToCroOptions([]);
      }
      if (response.data.centerData) {
        setCenters(response.data.centerData);
      } else {
        setCenters([]);
      }
    } catch (error) {
      console.error("Error getting To CRO data:", error);
      toast.error("Error loading CRO centers");
    }
  };

  const getFromCroData = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/getCroData`);
      if (response.data.message) {
        setFromCroOptions(transformCroData(response.data.message));
      } else {
        setFromCroOptions([]);
      }
    } catch (error) {
      console.error("Error fetching From CRO options:", error);
    }
  }, []);

  useEffect(() => {
    getFromCroData();
  }, [getFromCroData]);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  const handleFromCroChange = (selectedOption) => {
    setSelectedFromCro(selectedOption);
    setSelectedToCro(null);
    setSelectedCenters([]);
    setSearchTerm("");
    if (selectedOption) {
      getToCroData(selectedOption.branchId, selectedOption.value);
    } else {
      setToCroOptions([]);
      setCenters([]);
    }
  };

  const handleToCroChange = (selectedOption) => {
    setSelectedToCro(selectedOption);
  };

  const handleCenterChange = (centerId) => {
    if (selectedCenters.includes(centerId)) {
      setSelectedCenters(selectedCenters.filter((id) => id !== centerId));
    } else {
      setSelectedCenters([...selectedCenters, centerId]);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedCenters(filteredCenters.map((c) => c.id));
    } else {
      setSelectedCenters([]);
    }
  };

  const handleReset = () => {
    setSelectedFromCro(null);
    setSelectedToCro(null);
    setToCroOptions([]);
    setCenters([]);
    setSelectedCenters([]);
    setSearchTerm("");
    getFromCroData();
  };

  const handleSubmit = async () => {
    if (!selectedFromCro?.value) {
      toast.warning("Please select a 'From' CRO.");
      return;
    }
    if (!selectedToCro?.value) {
      toast.warning("Please select a 'To' CRO.");
      return;
    }
    if (selectedCenters.length === 0) {
      toast.warning("Please select at least one center.");
      return;
    }

    try {
      const response = await axios.post(`${BASE_URL}/api/transferCro`, {
        fromCroId: selectedFromCro.value,
        toCroId: selectedToCro.value,
        transferType: "center",
        selectedCenters: selectedCenters,
      });
      if (response.data.message) {
        toast.success("Centers transferred successfully!");
        setTimeout(() => {
          window.location.reload();
        }, 1200);
      }
    } catch (error) {
      console.error("Transfer error:", error);
      toast.error(error.response?.data?.error || "Failed to transfer centers");
    }
  };

  const filteredCenters = useMemo(() => {
    if (!searchTerm.trim()) return centers;
    const q = searchTerm.toLowerCase().trim();
    return centers.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [centers, searchTerm]);

  const isAllSelected =
    filteredCenters.length > 0 &&
    filteredCenters.every((c) => selectedCenters.includes(c.id));

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section inline-header">
            <h1 className="TableHeaderText m-0">Transfer CRO Center Wise</h1>
          </div>

          {/* CRO Selection Bar */}
          <div className="master-search-section py-3 px-4">
            <Row className="w-100 g-3 align-items-center">
              <Col xs={12} md={6}>
                <label className="form-label fw-semibold text-muted mb-1">
                  From CRO
                </label>
                <Select
                  value={selectedFromCro}
                  onChange={handleFromCroChange}
                  options={fromCroOptions}
                  placeholder="Select source CRO..."
                  isClearable
                  className="filter-dropdown"
                />
              </Col>
              <Col xs={12} md={6}>
                <label className="form-label fw-semibold text-muted mb-1">
                  To CRO
                </label>
                <Select
                  value={selectedToCro}
                  onChange={handleToCroChange}
                  options={toCroOptions}
                  placeholder="Select destination CRO..."
                  isClearable
                  isDisabled={!selectedFromCro}
                  className="filter-dropdown"
                />
              </Col>
            </Row>
          </div>

          {/* Center Selection Section */}
          <div className="master-list-section">
            {centers.length > 0 ? (
              <div className="w-100">
                {/* Search & Select All Bar */}
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-3">
                    <Form.Check
                      type="checkbox"
                      id="select-all-centers"
                      label={`Select All (${selectedCenters.length}/${filteredCenters.length})`}
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      className="fw-semibold"
                    />
                  </div>

                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      placeholder="Search centers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                    <span className="search-icon">
                      <IoSearch />
                    </span>
                  </div>
                </div>

                {/* Table for clean list */}
                <div className="master-table-wrapper table-responsive">
                  <table className="mx-auto table table-striped table-list">
                    <thead>
                      <tr>
                        <th style={{ width: "60px" }}>Select</th>
                        <th style={{ width: "70px" }}>No</th>
                        <th>Center Name</th>
                        <th>Meeting Day</th>
                        <th>Meeting Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCenters.length > 0 ? (
                        filteredCenters.map((center, index) => {
                          const isChecked = selectedCenters.includes(center.id);
                          return (
                            <tr
                              key={center.id}
                              onClick={() => handleCenterChange(center.id)}
                              style={{ cursor: "pointer" }}
                            >
                              <td onClick={(e) => e.stopPropagation()}>
                                <Form.Check
                                  type="checkbox"
                                  id={`center-${center.id}`}
                                  checked={isChecked}
                                  onChange={() => handleCenterChange(center.id)}
                                />
                              </td>
                              <td>{index + 1}</td>
                              <td className="fw-semibold text-start">
                                {center.name}
                              </td>
                              <td>{center.bmMeetingDayOrder || "-"}</td>
                              <td>{center.bmMeetingTime || "-"}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center py-4 text-muted">
                            No centers match the search filter
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-5 text-muted">
                {selectedFromCro
                  ? "No centers found for the selected CRO."
                  : "Please select a 'From CRO' to view and assign centers."}
              </div>
            )}
          </div>

          {/* Action Footer */}
          <div className="master-pagination-wrapper py-3">
            <div className="d-flex justify-content-center align-items-center gap-3">
              <button
                type="button"
                className="btn btn-addCredential m-0"
                onClick={handleSubmit}
                disabled={selectedCenters.length === 0 || !selectedToCro}
              >
                Transfer Selected Centers ({selectedCenters.length})
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleReset}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position="top-center" />
    </section>
  );
};

export default TransferCroCenterWise;
